import React, { useState, memo, useRef, useEffect } from 'react';
import Modal from 'components/shared/Modal/Modal';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import moment from 'moment';
import { LOADING_CIRCLE, USER_PREFERENCE_SETS } from 'common/utils/constants';
import { v4 } from 'uuid';
import classnames from 'classnames';
import { getTrimmedString } from '../FiltersBar/utils';
import { SPECIAL_CHARS_FORMAT } from 'common/utils/constants';
import { logger } from 'common/utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { updateDevicesPageManageFilterModal, addToastNotification, deleteToastNotification } from 'redux/actions';
import ToastNotificationActionTypes from 'components/shared/ToastNotifications/constants';
import './ManageFilterSetsModal.css';
import { selectUserPreferenceList, patchUserPreference, deleteUserPreference } from '../../../redux/UserPreferences';
import { selectUserPreferences } from '../../../redux/selectors';

const ManageFilterSetsModal = () => {
  const mountedRef = useRef(false);
  const dispatch = useDispatch();
  const isModalOpen = useSelector((state) => state.ui.devicesPage.manageFilterSetsModal);
  const allFilterSets = useSelector((state) =>
    selectUserPreferenceList(selectUserPreferences(state), USER_PREFERENCE_SETS.FILTER_SET)
  );
  const [isEditing, setIsEditing] = useState(new Array(allFilterSets.length || 0).fill(false));
  const [inputValues, setInputValues] = useState(allFilterSets.map((filter) => filter.name) || []);
  const [inputErrors, setInputErrors] = useState(new Array(allFilterSets.length || 0).fill(null));
  const [actionPending, setActionPending] = useState(false);
  const [deleteNotificationId, setDeleteNotificationId] = useState('');

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Updates input value and potential error message
   * @param {string} targetString
   * @param {number} index
   */
  const inputOnChange = (targetString, index) => {
    setInputValues([...inputValues.slice(0, index), targetString, ...inputValues.slice(index + 1)]);

    let errorMessage;

    if (targetString.length > 50) {
      errorMessage = 'Number of characters should NOT be more than 50.';
    } else if (targetString.length === 0) {
      errorMessage = 'Filter set name cannot be empty.';
    } else if (SPECIAL_CHARS_FORMAT.test(targetString)) {
      errorMessage = 'Filter set name should NOT contain special character.';
    } else if (isDuplicatedName(targetString, index)) {
      errorMessage = 'Filter set name already existed.';
    }
    setInputErrors([...inputErrors.slice(0, index), errorMessage, inputErrors.slice(index + 1)]);
  };

  const isDuplicatedName = (targetString, index) => {
    return (
      [...allFilterSets.slice(0, index), ...allFilterSets.slice(index + 1)].findIndex(
        (filter) => filter.name === targetString
      ) !== -1
    );
  };

  const onClose = () => {
    if (isModalOpen) dispatch(updateDevicesPageManageFilterModal(false));
  };

  /**
   * Removes delete/undo notification pop up to prevent edge cases
   */
  const deleteNotification = () => {
    if (deleteNotificationId === '') return;
    dispatch(deleteToastNotification(deleteNotificationId));
    setDeleteNotificationId('');
  };

  const handleNameChange = (index) => {
    const currFilterSet = allFilterSets[index];

    deleteNotification();
    setIsEditing([...isEditing.slice(0, index), false, ...isEditing.slice(index + 1)]);
    setActionPending(true);

    dispatch(
      patchUserPreference({
        preferenceId: currFilterSet.preferenceId,
        category: USER_PREFERENCE_SETS.FILTER_SET,
        name: inputValues[index]
      })
    )
      .then(() => {
        dispatch(
          addToastNotification({
            id: v4(),
            title: currFilterSet.name,
            description: 'changed its name successfully.'
          })
        );
      })
      .catch((error) => {
        logger.error(error);

        dispatch(
          addToastNotification({
            id: v4(),
            title: currFilterSet.name,
            description: 'failed to change its name. Try again'
          })
        );
      })
      .finally(() => {
        if (mountedRef.current) {
          setActionPending(false);
        }
      });
  };

  const handleDefaultChange = (index) => {
    const currFilterSet = allFilterSets[index];

    deleteNotification();
    setIsEditing(isEditing.map(() => false));
    setActionPending(true);

    dispatch(
      patchUserPreference({
        preferenceId: currFilterSet.preferenceId,
        category: USER_PREFERENCE_SETS.FILTER_SET,
        default: !allFilterSets[index].default
      })
    )
      .then(() => {
        dispatch(
          addToastNotification({
            id: v4(),
            title: currFilterSet.name,
            description: !allFilterSets[index].default
              ? 'is set as the new default list.'
              : 'is unset as the default list.'
          })
        );
      })
      .catch((error) => {
        logger.error(error);

        dispatch(
          addToastNotification({
            id: v4(),
            title: currFilterSet.name,
            description: `Failed to set default for`,
            reverse: true
          })
        );
      })
      .finally(() => {
        if (mountedRef.current) {
          setActionPending(false);
        }
      });
  };

  const handleDelete = (index) => {
    const currFilterSet = allFilterSets[index];

    deleteNotification();
    setIsEditing(isEditing.map(() => false));
    setActionPending(true);

    dispatch(
      deleteUserPreference({
        preferenceId: currFilterSet.preferenceId,
        category: USER_PREFERENCE_SETS.FILTER_SET,
        default: currFilterSet.default
      })
    )
      .then(() => {
        const notificationId = v4();

        if (mountedRef.current) {
          setDeleteNotificationId(notificationId);
        }

        dispatch(
          addToastNotification({
            id: notificationId,
            title: currFilterSet.name,
            description: 'was removed.',
            actionMessage: 'Undo',
            actionType: ToastNotificationActionTypes.UNDO_REMOVED_FILTER_SET,
            preferenceId: currFilterSet.preferenceId,
            preferenceCategory: USER_PREFERENCE_SETS.FILTER_SET,
            groupName: currFilterSet.name,
            callback: (isActionPending) => {
              if (mountedRef.current) {
                setActionPending(isActionPending);
              }
            }
          })
        );
      })
      .catch((error) => {
        logger.error(error);

        dispatch(
          addToastNotification({
            id: v4(),
            title: currFilterSet.name,
            description: `  failed to be removed`
          })
        );
      })
      .finally(() => {
        if (mountedRef.current) {
          setActionPending(false);
        }
      });
  };

  const getSingleActiveFilterDiv = ({ filterName, displayActiveFilters }) => {
    if (!displayActiveFilters || displayActiveFilters.length === 0) {
      return (
        <div className="ManageFilterSetsModal_Content_Item_Bottom_ActiveFilters_EmptySingleFilter" key={filterName}>
          {filterName}
        </div>
      );
    }

    const displayString =
      filterName === 'Provisioned on'
        ? displayActiveFilters.map((date) => moment(date).format('M/D/YYYY')).join(' - ')
        : displayActiveFilters.join(', ');

    return (
      <div className="ManageFilterSetsModal_Content_Item_Bottom_ActiveFilters_ActiveSingleFilter" key={filterName}>
        <div className="FilterName">{filterName}</div>
        <div className="ActiveFilters" data-tip={displayString}>
          {filterName === 'Provisioned on' ? displayString : getTrimmedString(displayString, 20)}
        </div>
      </div>
    );
  };

  const getItemDiv = (item, index) => {
    const parsedActiveFilters = [
      {
        filterName: 'Connection status',
        displayActiveFilters: item.state.connectionStatus
      },
      {
        filterName: 'Admin state',
        displayActiveFilters: item.state.adminState
      },
      { filterName: 'Operational state', displayActiveFilters: item.state.operationalState },
      { filterName: 'Device type', displayActiveFilters: item.state.deviceType },
      { filterName: 'Provisioned on', displayActiveFilters: item.state.provisionedOn },
      { filterName: 'Alarms', displayActiveFilters: item.state.alarms }
    ];

    return (
      <div className="ManageFilterSetsModal_Content_Item" key={index}>
        <div className="ManageFilterSetsModal_Content_Item_Header">
          {isEditing[index] && (
            <div className="ManageFilterSetsModal_Content_Item_Header_Container">
              <div className="ManageFilterSetsModal_Content_Item_Header_Input">
                <input
                  onChange={(evt) => inputOnChange(evt.target.value, index)}
                  value={inputValues[index]}
                  type="text"
                />
                {!inputErrors[index] && inputValues[index] !== allFilterSets[index].name ? (
                  <SvgAF img={IconLoader.icon_commit_edit} onClick={() => handleNameChange(index)} />
                ) : (
                  <SvgAF img={IconLoader.icon_commit_disabled} />
                )}
                <SvgAF
                  img={IconLoader.icon_cancel_edit}
                  onClick={() => {
                    setIsEditing([...isEditing.slice(0, index), false, ...isEditing.slice(index + 1)]);
                    setInputValues([
                      ...inputValues.slice(0, index),
                      allFilterSets[index].name,
                      ...inputValues.slice(index + 1)
                    ]);
                    setInputErrors([...inputErrors.slice(0, index), null, ...inputErrors.slice(index + 1)]);
                  }}
                />
              </div>

              {inputErrors[index] && <div className="ManageFilterSetsModal_ErrorMessage">{inputErrors[index]}</div>}
            </div>
          )}

          {!isEditing[index] && <div className="ManageFilterSetsModal_Content_Item_Header_Text">{item.name}</div>}
          {!isEditing[index] && (
            <div
              className={classnames({
                ManageFilterSetsModal_Content_Item_Header_EditButton: true
              })}
            >
              <SvgAF
                img={IconLoader.icon_edit_name}
                hover={IconLoader.icon_edit_name_hover}
                onClick={() => {
                  inputOnChange(allFilterSets[index].name, index);
                  setIsEditing([...isEditing.map((flag, i) => (i === index ? true : false))]);
                }}
              />
            </div>
          )}
          <div
            className={classnames({
              ManageFilterSetsModal_Content_Item_Header_Default: true
            })}
            onClick={() => handleDefaultChange(index)}
          >
            {
              <SvgAF
                img={item.default ? IconLoader.bttn_make_default_select : IconLoader.bttn_make_default}
                hover={item.default ? IconLoader.bttn_make_default_select : IconLoader.bttn_make_default_hover}
              />
            }
          </div>
        </div>
        <div className="ManageFilterSetsModal_Content_Item_Bottom">
          <div className="ManageFilterSetsModal_Content_Item_Bottom_ActiveFilters">
            {parsedActiveFilters.map((filter) => getSingleActiveFilterDiv(filter))}
          </div>
          <div
            className={classnames({
              ManageFilterSetsModal_Content_Item_Bottom_Delete: true
            })}
            onClick={() => handleDelete(index)}
          >
            <SvgAF img={IconLoader.icon_delete_list} hover={IconLoader.icon_delete_list_hover} />
          </div>
        </div>
      </div>
    );
  };

  let overlay = null;
  if (actionPending) {
    overlay = <div className="Global_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
  }
  return (
    <Modal animateX={false} global close={onClose} noPadding height={800} width={1024}>
      <div className="ManageFilterSetsModal">
        {overlay}
        <div className="ManageFilterSetsModal_Header">Manage filter sets</div>
        <div
          className={classnames({
            ManageFilterSetsModal_Content: true,
            ManageFilterSetsModal_Content_ActionPending: actionPending
          })}
        >
          <PerfectScrollbar>
            {allFilterSets.length === 0 ? (
              <div className="ManageFilterSet_NoListsCreatedDiv">No filter set has been created.</div>
            ) : (
              allFilterSets.map((item, index) => getItemDiv(item, index))
            )}
          </PerfectScrollbar>
        </div>
      </div>
    </Modal>
  );
};

export default memo(ManageFilterSetsModal);
